/**
 * A complete backend that runs in the browser — used when the API is
 * unreachable (docs/GAME_DESIGN.md § 7). The UI labels it "offline demo".
 *
 * - The local player, their runs, issued sessions and schools they added live
 *   in storage ("dropstack.offline.v1"); ratings live in local progress.
 * - Leaderboards mix the local player with ~400 fictional demo players
 *   (services/demoData.ts), ranked by the same rules as the server
 *   (./leaderboard.ts).
 * - The local player's first completed run is a placement run (GAME_DESIGN § 5):
 *   it is stored and sets the level, but never enters a leaderboard.
 * - "More time" (relaxed) sessions (GAME_DESIGN § 4.1) are replayed at the
 *   relaxed pace they were issued with; their runs are stored and rated but,
 *   like placement runs, never ranked.
 * - Runs are validated with the server's own code: core validateRun replays the
 *   event log and recomputes the summary, so the offline demo can't be "won" by
 *   a tampered summary any more than the real server can.
 */
import { ANTICHEAT, paceConfig, RULES_VERSION, validateRun, type Locale, type RunSummary } from '@dropstack/core';
import type {
  BackendClient,
  LeaderboardPage,
  LeaderboardQuery,
  PlayerProfile,
  ProfileInput,
  SchoolOption,
  SessionTicket,
  StartSessionOptions,
  SubmitResult,
} from '../../contracts.ts';
import { localRatings, getProgressStore, type ProgressStore } from '../../state/progress.ts';
import { loadSettings } from '../../state/settings.ts';
import { getAppStorage, isFiniteNumber, isRecord, STORAGE_KEYS, type JsonStore } from '../../state/storage.ts';
import { generateDemoData, type DemoData } from '../demoData.ts';
import { citiesFor, findSaProvince, HOME_COUNTRY } from '../geo.ts';
import { validateProfileInput } from '../profileValidation.ts';
import { randomId, randomSeed } from '../random.ts';
import { foldText, rankMatches } from '../text.ts';
import { CITY_RESULTS_MAX, SCHOOL_RESULTS_MAX } from './api-types.ts';
import { decodeProfile } from './decode.ts';
import { BackendError } from './errors.ts';
import { buildLeaderboard, ranksFor, type BoardPlayer, type BoardRun } from './leaderboard.ts';

export interface OfflineDemoBackendOptions {
  /** Persistence (default: the app's localStorage-backed store). */
  store?: JsonStore;
  /** Local ratings/progress (default: the app-wide progress store). */
  progress?: ProgressStore;
  /** Wall clock, epoch ms (default Date.now). */
  now?: () => number;
  /** Seed source for new sessions (default: crypto.getRandomValues). */
  seed?: () => number;
  /** Language for a newly created local player (default: saved settings). */
  locale?: () => Locale;
  /** Demo population (default: generated from DEMO_SEED around the current time; null = none). */
  demo?: DemoData | null;
}

/** Sessions are kept only as long as they could still be submitted, and at most this many. */
const MAX_SESSIONS = 10;

interface IssuedSession {
  sessionId: string;
  token: string;
  seed: number;
  startRating: number;
  referenceRating: number;
  issuedAt: number;
  used: boolean;
  /** "More time" session: replayed at the relaxed pace, never ranked. */
  relaxed: boolean;
}

interface OfflineDb {
  player: PlayerProfile | null;
  runs: BoardRun[];
  sessions: IssuedSession[];
  /** Schools the local player added during onboarding. */
  schools: SchoolOption[];
}

export class OfflineDemoBackend implements BackendClient {
  readonly kind = 'offline-demo' as const;

  private readonly store: JsonStore;
  private readonly progress: ProgressStore;
  private readonly now: () => number;
  private readonly seed: () => number;
  private readonly locale: () => Locale;
  private readonly demo: DemoData;

  constructor(options: OfflineDemoBackendOptions = {}) {
    this.store = options.store ?? getAppStorage();
    this.progress = options.progress ?? getProgressStore();
    this.now = options.now ?? Date.now;
    this.seed = options.seed ?? randomSeed;
    this.locale = options.locale ?? (() => loadSettings(this.store).locale);
    this.demo =
      options.demo === undefined ? generateDemoData({ now: this.now() }) : (options.demo ?? { players: [], schools: [], runs: [] });
  }

  async ensurePlayer(): Promise<PlayerProfile> {
    return { ...this.player() };
  }

  async saveProfile(input: ProfileInput): Promise<PlayerProfile> {
    const checked = validateProfileInput(input);
    if (!checked.ok) {
      throw new BackendError('invalid-input', 'invalid-profile', 'Profile is not valid', null, checked.problems);
    }
    const value = checked.value;
    const current = this.player();
    const db = this.load();
    const { school, added } = this.resolveSchool(value);
    const profile: PlayerProfile = {
      ...current,
      displayName: value.displayName,
      grade: value.grade,
      schoolId: school.id,
      schoolName: school.name,
      city: value.city,
      province: value.province,
      country: value.country,
      leaderboardOptIn: value.leaderboardOptIn,
      locale: value.locale,
      onboarded: true,
    };
    this.save({ ...db, player: profile, schools: added ? [...db.schools, added] : db.schools });
    return { ...profile };
  }

  async searchSchools(q: string, filter: { country?: string; province?: string; city?: string }): Promise<SchoolOption[]> {
    const matches = this.allSchools().filter((s) => inPlace(s, filter));
    return rankMatches(matches, q, (s) => s.name, SCHOOL_RESULTS_MAX).map(toSchoolOption);
  }

  async suggestCities(q: string, filter: { country: string; province?: string }): Promise<string[]> {
    const known = citiesFor(filter.country, filter.province);
    const fromSchools = this.allSchools()
      .filter((s) => inPlace(s, filter))
      .map((s) => s.city);
    const unique = new Map<string, string>();
    for (const city of [...known, ...fromSchools]) if (!unique.has(foldText(city))) unique.set(foldText(city), city);
    return rankMatches([...unique.values()], q, (c) => c, CITY_RESULTS_MAX);
  }

  async startSession(options: StartSessionOptions = {}): Promise<SessionTicket> {
    this.player(); // sessions belong to a player, as on the server
    const relaxed = options.relaxed === true;
    const { startRating, referenceRating } = localRatings(this.progress.get());
    const issuedAt = this.now();
    const session: IssuedSession = {
      sessionId: randomId('offline-session'),
      token: randomId('offline-token'),
      seed: this.seed() >>> 0,
      startRating,
      referenceRating,
      issuedAt,
      used: false,
      relaxed,
    };
    const db = this.load();
    // Used sessions stay until they expire, so a replayed submit says "session-used".
    const live = db.sessions.filter((s) => issuedAt - s.issuedAt < ANTICHEAT.SESSION_TTL_MS);
    this.save({ ...db, sessions: [...live, session].slice(-MAX_SESSIONS) });
    return {
      sessionId: session.sessionId,
      token: session.token,
      seed: session.seed,
      startRating,
      referenceRating,
      rulesVersion: RULES_VERSION,
      issuedAt,
      offline: false,
      relaxed,
    };
  }

  async submitRun(ticket: SessionTicket, summary: RunSummary): Promise<SubmitResult> {
    const player = this.player();
    const db = this.load();
    const refuse = (reason: string): SubmitResult => ({
      accepted: false,
      flagged: false,
      runId: null,
      raw: summary.raw,
      rated: summary.rated,
      savedRating: this.progress.get().savedRating ?? 0,
      ranks: {},
      personalBest: false,
      placement: false,
      // Before the session is found, the ticket is all there is to go on.
      relaxed: ticket.relaxed,
      reason,
    });

    const session = db.sessions.find((s) => s.sessionId === ticket.sessionId && s.token === ticket.token);
    if (!session) return refuse('session-unknown');
    if (session.used) return refuse('session-used');
    const now = this.now();
    if (now - session.issuedAt > ANTICHEAT.SESSION_TTL_MS) return refuse('session-expired');
    // Single use: burn the session before validating, exactly like the server.
    const burned: OfflineDb = { ...db, sessions: db.sessions.map((s) => (s === session ? { ...s, used: true } : s)) };
    this.save(burned);

    const report = validateRun({
      rulesVersion: summary.rulesVersion,
      seed: session.seed,
      startRating: session.startRating,
      referenceRating: session.referenceRating,
      events: summary.events,
      claimed: { raw: summary.raw, rated: summary.rated },
      wallClockMs: now - session.issuedAt,
      // The pace the session was issued with, not the ticket's say-so.
      config: paceConfig(session.relaxed),
    });
    const verified = report.summary;
    if (!report.ok || !verified) {
      const reason = report.anomalies.find((a) => a.severity === 'reject')?.code ?? 'rejected';
      return {
        ...refuse(reason),
        raw: verified?.raw ?? summary.raw,
        rated: verified?.rated ?? summary.rated,
        relaxed: session.relaxed,
      };
    }

    const mine = burned.runs.filter((r) => r.playerId === player.playerId);
    // Placement: the first completed (stored) run sets the level but is never ranked.
    const placement = mine.length === 0;
    const previous = mine.filter((r) => !r.flagged && r.placement !== true && r.relaxed !== true);
    const ranked = !report.flagged && !placement && !session.relaxed;
    const run: BoardRun = {
      runId: randomId('offline-run'),
      playerId: player.playerId,
      raw: verified.raw,
      rated: verified.rated,
      levelReached: verified.levelReached,
      at: now,
      flagged: report.flagged,
      placement,
      relaxed: session.relaxed,
    };
    this.save({ ...burned, runs: [...burned.runs, run] });
    this.progress.recordRating(verified.savedRating);

    const scored = verified.raw > 0 || verified.rated > 0;
    const personalBest =
      ranked &&
      scored &&
      (previous.length === 0 ||
        verified.rated > Math.max(...previous.map((r) => r.rated)) ||
        verified.raw > Math.max(...previous.map((r) => r.raw)));

    return {
      accepted: true,
      flagged: report.flagged,
      runId: run.runId,
      raw: verified.raw,
      rated: verified.rated,
      savedRating: verified.savedRating,
      ranks: ranked ? ranksFor({ ...this.board(), me: player, now }) : {},
      personalBest,
      placement,
      relaxed: session.relaxed,
    };
  }

  async getLeaderboard(q: LeaderboardQuery): Promise<LeaderboardPage> {
    const board = this.board();
    return buildLeaderboard({ ...board, query: q, now: this.now(), source: 'offline-demo' });
  }

  /** Forget the local player, runs and sessions (Settings → reset). Demo players stay. */
  resetLocalData(): void {
    this.store.remove(STORAGE_KEYS.offlineDb);
  }

  // ───────────── internals ─────────────

  /** The local player, created (anonymous, not onboarded) on first use. */
  private player(): PlayerProfile {
    const db = this.load();
    if (db.player) return db.player;
    const player: PlayerProfile = {
      playerId: randomId('local-player'),
      onboarded: false,
      displayName: '',
      grade: null,
      schoolId: null,
      schoolName: '',
      city: '',
      province: '',
      country: HOME_COUNTRY,
      leaderboardOptIn: false,
      locale: this.locale(),
    };
    this.save({ ...db, player });
    return player;
  }

  /** Everyone on the local boards: demo players plus the local player. */
  private board(): { players: readonly BoardPlayer[]; runs: BoardRun[]; me: PlayerProfile | null } {
    const db = this.load();
    return {
      players: db.player ? [...this.demo.players, db.player] : this.demo.players,
      runs: [...this.demo.runs, ...db.runs],
      me: db.player,
    };
  }

  private allSchools(): SchoolOption[] {
    return [...this.demo.schools.map(toSchoolOption), ...this.load().schools];
  }

  /**
   * Link a known school by id, else the same-named school in the same city,
   * else register a new local school (the rules the server applies).
   */
  private resolveSchool(value: ProfileInput): { school: SchoolOption; added: SchoolOption | null } {
    const all = this.allSchools();
    const byId = value.schoolId ? all.find((s) => s.id === value.schoolId) : undefined;
    if (byId) return { school: byId, added: null };
    const sameName = all.find(
      (s) =>
        foldText(s.name) === foldText(value.schoolName) &&
        foldText(s.city) === foldText(value.city) &&
        s.country === value.country,
    );
    if (sameName) return { school: sameName, added: null };
    const school: SchoolOption = {
      id: randomId('local-school'),
      name: value.schoolName,
      city: value.city,
      province: value.province,
      country: value.country,
    };
    return { school, added: school };
  }

  private load(): OfflineDb {
    const db = this.store.get(STORAGE_KEYS.offlineDb, decodeDb);
    return db ?? { player: null, runs: [], sessions: [], schools: [] };
  }

  private save(db: OfflineDb): void {
    this.store.set(STORAGE_KEYS.offlineDb, db);
  }
}

// ───────────── helpers ─────────────

function toSchoolOption(s: SchoolOption): SchoolOption {
  return { id: s.id, name: s.name, city: s.city, province: s.province, country: s.country };
}

/** Does a school match an optional country/province/city filter? */
function inPlace(s: SchoolOption, filter: { country?: string; province?: string; city?: string }): boolean {
  if (filter.country && s.country.toUpperCase() !== filter.country.trim().toUpperCase()) return false;
  if (filter.province) {
    const want = findSaProvince(filter.province)?.code ?? foldText(filter.province);
    const have = findSaProvince(s.province)?.code ?? foldText(s.province);
    if (want !== have) return false;
  }
  return !filter.city || foldText(filter.city) === foldText(s.city);
}

/** Decode the stored db leniently: malformed entries are dropped, never fatal. */
function decodeDb(raw: unknown): OfflineDb | null {
  if (!isRecord(raw)) return null;
  let player: PlayerProfile | null = null;
  try {
    player = raw.player ? decodeProfile(raw.player) : null;
  } catch {
    player = null;
  }
  const array = (x: unknown): unknown[] => (Array.isArray(x) ? x : []);
  const runs = array(raw.runs).flatMap((r): BoardRun[] =>
    isRecord(r) &&
    typeof r.runId === 'string' &&
    typeof r.playerId === 'string' &&
    isFiniteNumber(r.raw) &&
    isFiniteNumber(r.rated) &&
    isFiniteNumber(r.levelReached) &&
    isFiniteNumber(r.at)
      ? [
          {
            runId: r.runId,
            playerId: r.playerId,
            raw: r.raw,
            rated: r.rated,
            levelReached: r.levelReached,
            at: r.at,
            flagged: r.flagged === true,
            placement: r.placement === true,
            relaxed: r.relaxed === true,
          },
        ]
      : [],
  );
  const sessions = array(raw.sessions).flatMap((s): IssuedSession[] =>
    isRecord(s) &&
    typeof s.sessionId === 'string' &&
    typeof s.token === 'string' &&
    isFiniteNumber(s.seed) &&
    isFiniteNumber(s.startRating) &&
    isFiniteNumber(s.referenceRating) &&
    isFiniteNumber(s.issuedAt)
      ? [
          {
            sessionId: s.sessionId,
            token: s.token,
            seed: s.seed,
            startRating: s.startRating,
            referenceRating: s.referenceRating,
            issuedAt: s.issuedAt,
            used: s.used === true,
            relaxed: s.relaxed === true,
          },
        ]
      : [],
  );
  const schools = array(raw.schools).flatMap((s): SchoolOption[] =>
    isRecord(s) &&
    typeof s.id === 'string' &&
    typeof s.name === 'string' &&
    typeof s.city === 'string' &&
    typeof s.province === 'string' &&
    typeof s.country === 'string'
      ? [{ id: s.id, name: s.name, city: s.city, province: s.province, country: s.country }]
      : [],
  );
  return { player, runs, sessions, schools };
}
